import { Form, Input, Select, Table, Tag } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const { RangePicker } = DatePicker;
const { Option } = Select;

const StudentAttendance = ({ setStep }) => {
  const [form] = Form.useForm();
  const [selectedStudentId, setSelectedStudentId] = useState();
  const [selectedStudent, setSelectedStudent] = useState();
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: moment().subtract(30, "days"),
    to: moment(),
  });

  const handleDateRangeChange = (dates) => {
    const [fromDate, toDate] = dates;
    setDateRange({ from: fromDate, to: toDate });
  };

  async function fetchStudent() {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/students/by-parent/`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data.result;
  }
  const students = useQuery("students/by-parent", fetchStudent);

  const handleSelectChange = (studentId) => {
    setSelectedStudentId(studentId);
    form.setFieldsValue({ name: studentId });
    const student =
      students?.data &&
      students?.data.length > 0 &&
      students?.data?.find((data) => data?._id === studentId);
    if (student) {
      setSelectedStudent(student?.class?.name);
    }
  };

  // Default to the first student once the list loads, so attendance
  // shows up immediately instead of requiring a manual selection.
  useEffect(() => {
    if (students?.data?.length > 0 && !selectedStudentId) {
      handleSelectChange(students.data[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students?.data]);

  useEffect(() => {
    const fetchStudentAttendance = async () => {
      const fromDate = dateRange.from.format("YYYY-MM-DD");
      const toDate = dateRange.to.format("YYYY-MM-DD");
      const res = await axios.get(
        `${serverUrl}/montessori/attendance/student/${selectedStudentId}?fromDate=${fromDate}&toDate=${toDate}`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      return res.data;
    };

    if (selectedStudentId && dateRange.from && dateRange.to) {
      fetchStudentAttendance()
        .then((response) => {
          const resultData = response?.result;
          const studentData =
            Array.isArray(resultData) && resultData.length > 0
              ? resultData[0]
              : resultData?.data?.[0] || {};

          const sortedAttendance = [...(studentData?.attendance || [])].sort(
            (a, b) => moment(b?.date).valueOf() - moment(a?.date).valueOf(),
          );

          setFilteredData(sortedAttendance);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [selectedStudentId, dateRange]);

  const columns = [
    {
      title: "Date",
      key: "date",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base font-medium">
          {moment(record?.date).format("DD MMM YYYY")}
        </div>
      ),
    },
    {
      title: "Check In",
      key: "check_in",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_in ? (
            <div className="flex flex-col">
              <span className="text-green-600 font-medium">✓ Checked In</span>
              {record.check_in_time && (
                <span className="text-xs text-gray-400">
                  {moment(record.check_in_time).format("LT")}
                  {record.check_in_by?.username &&
                    ` by ${record.check_in_by.username}`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">✗ Not Checked In</span>
          )}
        </div>
      ),
    },
    {
      title: "Check Out",
      key: "check_out",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_out ? (
            <div className="flex flex-col">
              <span className="text-blue-600 font-medium">✓ Checked Out</span>
              {record.check_out_time && (
                <span className="text-xs text-gray-400">
                  {moment(record.check_out_time).format("LT")}
                  {record.check_out_by?.username &&
                    ` by ${record.check_out_by.username}`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">✗ Not Checked Out</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 120,
      render: (record) => {
        if (record?.holiday?.isHoliday) {
          return <Tag color="blue">Holiday</Tag>;
        }
        if (record?.permission?.hasPermission) {
          return <Tag color="orange">{record.permission.permissionType}</Tag>;
        }
        // A day with no record is not the same as a recorded absence: the
        // server sends gap-filled days without an _id, and showing those as
        // "Absent" would accuse the school of marking a child away when in
        // fact nobody ever took the register.
        if (!record?._id) {
          return <Tag color="default">Not marked</Tag>;
        }
        const morningExcused = !!record?.permission?.morningPermission && !record?.is_checked_in;
        const afternoonExcused = !!record?.permission?.afternoonPermission && !record?.is_checked_out;
        if (morningExcused || afternoonExcused) {
          const otherOk =
            (morningExcused || record?.is_checked_in) &&
            (afternoonExcused || record?.is_checked_out);
          return (
            <Tag
              color={
                morningExcused && afternoonExcused
                  ? "blue"
                  : otherOk
                    ? "orange"
                    : "red"
              }
            >
              {morningExcused && afternoonExcused
                ? "Permission"
                : otherOk
                  ? "Half Day (Permission)"
                  : "Absent"}
            </Tag>
          );
        }

        if (record?.is_checked_in && record?.is_checked_out) {
          return <Tag color="green">Present</Tag>;
        } else if (record?.is_checked_in || record?.is_checked_out) {
          return <Tag color="gold">Partial</Tag>;
        } else {
          return <Tag color="red">Absent</Tag>;
        }
      },
    },
  ];

  return (
    <div className="attendance">
      <div className="pt-8">
        <div className="bg-white p-10 rounded-2xl">
          <div className="text-2xl text-orange-500 font-semibold pb-4">
            Attendance
          </div>
          <Form
            layout="vertical"
            name="basic"
            form={form}
            autoComplete="off"
            className="w-1/2"
          >
            <Form.Item
              label="Name"
              name="name"
              className="w-full"
              rules={[
                {
                  required: true,
                  message: "Please select name",
                },
              ]}
            >
              <Select
                showSearch
                showArrow
                placeholder="Select name"
                className="w-full bg-gray-300"
                style={{
                  border: "6px",
                  backgroundColor: "#EBEBEB",
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value) => handleSelectChange(value)}
              >
                {students &&
                  students?.data?.map((data) => {
                    return (
                      <Option key={data._id} value={data._id}>
                        {`${data?.first_name} ${data?.last_name}`}
                      </Option>
                    );
                  })}
              </Select>
            </Form.Item>
            <Form.Item label="Class" className="w-full">
              <Input readonly value={selectedStudent} />
            </Form.Item>
            <Form.Item label="Choose a range" name="date" className="w-full">
              <RangePicker
                format={"DD/MM/YYYY"}
                disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
                className="w-full"
                onChange={handleDateRangeChange}
              />
            </Form.Item>
          </Form>
          <div className="pt-5 attendance-table">
            <Table
              columns={columns}
              dataSource={
                filteredData && filteredData.length > 0 ? filteredData : []
              }
              loading={!selectedStudentId}
              rowKey={(record) => record._id || record.date}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
